import React, { FC, useMemo } from 'react';
import { DraggableItem } from '../../../../shared/components';
import { ExerciseWithNotes } from '../../../../../config/api/models/Exercise';
import { GroupStudentSeriesScores } from '../../../../../config/api/models/Score';
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import ExerciseDetailContent from './ExerciseDetailContent';
import { TeacherFiltersEnum } from '../../../../shared/constants';
import { BlockNote } from '../../../../../config/api/models/BlockNote';
import { Feedback } from '../../../../../config/api/models/Feedback';

import { useStudentFeedbackForTrajectory, useTeacherFeedback } from 'src/modules/feedback/hooks';
import { useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';
import { useUser } from 'src/modules/shared';
import { Indicators } from 'src/modules/shared/components/Indicators';
import { ExerciseDetailRelativeWrapper } from './Atoms';
import { useDisclosure } from 'react-use-disclosure';
import { FeedbackModule } from 'src/modules/feedback';
import { TrajectorySectionOrWorksheetType } from 'src/config/api/models/Trajectory';

type Props = {
  index: number;
  expanded: string | false;
  onChangeExpanded: (panel: string) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => void;
  seriesId: string;
  pageType: TrajectorySectionOrWorksheetType;
  exercise: ExerciseWithNotes;
  mode: 'read' | 'edit';
  teacherProgressAndScores?: GroupStudentSeriesScores;
  onUpdateExercise?: () => void;
  dragHandleProps?: DraggableProvidedDragHandleProps;
  note?: BlockNote | undefined;
  onSetExpanded?: (id?: string | false) => void;
};

let feedbackContent: Feedback[] | undefined = [];

const ExerciseDetail: FC<Props> = ({
  index,
  expanded,
  onChangeExpanded,
  onUpdateExercise,
  seriesId,
  pageType,
  exercise,
  mode,
  dragHandleProps,
  teacherProgressAndScores,

  onSetExpanded = (id) => {},
}) => {
  const activeFilter = useAppSelector((state) => state.filters.activeFilter);
  const selectedGroup = useAppSelector((state) => state.filters.selectedGroup);
  const { isTeacher, isStudent } = useUser();

  const { feedback: teacherFeedback } = useTeacherFeedback();

  const { feedback: studentFeedback } = useStudentFeedbackForTrajectory();

  if (isStudent) {
    feedbackContent = studentFeedback
      ?.find((item) => item.pageId === seriesId)
      ?.feedback?.blockFeedbacks.filter((item) => item.blockId === exercise.id);
  }
  if (isTeacher) {
    feedbackContent = teacherFeedback
      ?.find((item) => item.pageId === seriesId)
      ?.blockFeedbacks?.find((item) => item.blockId === exercise.id)?.feedbacks;
  }

  const feedbackEnabled = useMemo(
    () =>
      (isTeacher && activeFilter === TeacherFiltersEnum.FOLLOW_UP && selectedGroup !== '') ||
      (isStudent && (feedbackContent?.length as number) > 0),
    [isTeacher, isStudent, activeFilter, selectedGroup]
  );

  const [feedbackBlockId, setFeedbackBlockId] = React.useState<string | undefined>(undefined);

  const {
    isOpen: feedbackIsOpen,
    close: closeFeedback,
    toggle: toggleFeedback,
    open: openFeedback,
  } = useDisclosure(false);

  React.useEffect(() => {
    if (exercise.id === feedbackBlockId) {
      openFeedback();

      setTimeout(() => {
        const indicator = document.querySelector(`#feedback_${exercise.id}`);
        if (indicator)
          indicator.scrollIntoView({
            block: 'center',
            inline: 'nearest',
            behavior: 'smooth',
          });
      }, 100);
    }
  }, [feedbackBlockId, exercise, openFeedback]);

  return (
    <>
      {
        {
          edit: (
            <DraggableItem dragHandleProps={dragHandleProps}>
              <ExerciseDetailContent
                index={index}
                expanded={expanded}
                onChangeExpanded={onChangeExpanded}
                seriesId={seriesId}
                pageType={pageType}
                exercise={exercise}
                mode={mode}
                teacherProgressAndScores={teacherProgressAndScores}
                onUpdateExercise={onUpdateExercise}
                feedbackEnabled={feedbackEnabled}
              />
            </DraggableItem>
          ),
          read: (
            <ExerciseDetailRelativeWrapper>
              {feedbackBlockId === exercise.id && feedbackEnabled && (
                <FeedbackModule
                  pageId={seriesId}
                  blockId={feedbackBlockId}
                  context="block"
                  mode="floating"
                  open={feedbackIsOpen}
                  onClose={closeFeedback}
                />
              )}

              <Indicators
                onClickFeedback={() => {
                  setFeedbackBlockId(exercise.id);
                  toggleFeedback();
                }}
                leftMargin={0}
                pageId={seriesId}
                block={exercise}
                expanded={expanded}
                handleExpand={(panel) => onSetExpanded(panel)}
              />
              <ExerciseDetailContent
                index={index}
                expanded={expanded}
                onChangeExpanded={onChangeExpanded}
                seriesId={seriesId}
                pageType={pageType}
                exercise={exercise}
                mode={mode}
                teacherProgressAndScores={teacherProgressAndScores}
                feedbackEnabled={feedbackEnabled}
              />
            </ExerciseDetailRelativeWrapper>
          ),
        }[mode]
      }
    </>
  );
};

export default ExerciseDetail;
